import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import {
	ExpansionPanel,
	ExpansionPanelDetails,
	ExpansionPanelSummary,
	Typography,
	Divider,
	Grid,
	Chip,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import EditIcon from "@material-ui/icons/Edit";
import {
	AttachmentListing,
	StyledIconButton,
	Lookup,
} from "frame/components";
import EquipmentModal from "./equipment-modal";
import { formatDate, canManageOrganisationRecords } from "utils";
import { useCurrentRole } from "utils/current-user";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
	root: {
		borderRadius: theme.spacing(1),
		border: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.white,
		overflow: "hidden",
		boxShadow: "none",
		"&:before": {
			display: "none",
		},
	},
	summary: {
		padding: theme.spacing(0, 1.5, 0, 2),
		minHeight: 52,
		"&.Mui-expanded": {
			minHeight: 52,
		},
		"& .MuiExpansionPanelSummary-content": {
			margin: theme.spacing(1.25, 0),
			alignItems: "center",
		},
	},
	summaryTitle: {
		...theme.typography.body1,
		fontWeight: 600,
		fontSize: "1rem",
		color: theme.palette.text.primary,
		flex: 1,
		minWidth: 0,
	},
	expandIcon: {
		color: theme.palette.text.secondary,
	},
	details: {
		padding: theme.spacing(0, 1.5, 2, 2),
		display: "block",
	},
	fieldsGrid: {
		marginTop: theme.spacing(2),
	},
	detailsActions: {
		display: "flex",
		justifyContent: "flex-end",
		marginTop: theme.spacing(1.5),
		marginRight: theme.spacing(-1.5),
	},
	fieldBlock: {
		minWidth: 0,
		width: "100%",
	},
	label: {
		color: "#999999",
		marginBottom: theme.spacing(0.25),
	},
	value: {
		color: theme.palette.brand,
		fontWeight: 500,
		wordBreak: "break-word",
	},
	chipActive: {
		backgroundColor: theme.palette.success.main,
		color: theme.palette.white,
	},
	chipInactive: {
		backgroundColor: theme.palette.error.main,
		color: theme.palette.white,
	},
	caption: {
		display: "block",
		color: "#999999",
	},
}));

const currentReadingLabel = (maintenanceType) =>
	({
		Hours: "Latest reading",
		Kms: "Latest reading",
		Month: "Last serviced",
	}[maintenanceType] || "Latest reading");

const EquipmentSummary = ({
	equipment,
	onSave,
	expanded,
	onExpandedChange,
}) => {
	const classes = useStyles();
	const role = useCurrentRole();
	const canManage = canManageOrganisationRecords(role);
	const [showEditModal, setShowEditModal] = React.useState(false);

	if (!equipment) {
		return null;
	}

	const handleModalSave = (equipmentItem) => {
		onSave(equipmentItem);
		setShowEditModal(false);
	};

	const rows = [
		{
			label: "Status",
			isStatus: true,
		},
		{
			label: "Type",
			value: equipment.type,
		},
		{
			label: "Make",
			value: equipment.make,
		},
		{
			label: "Model",
			value: equipment.model,
		},
		{
			label: "Serial / Rego",
			value: equipment.serialRego,
		},
		{
			label: "VIN",
			value: equipment.vin,
		},
		{
			label: "Asset Number",
			value: equipment.assetNumber,
		},
		{
			label: "Fuel Type",
			value: equipment.fuelType,
		},
		{
			label: "Purchase Date",
			value: equipment.purchaseDate
				? formatDate(equipment.purchaseDate)
				: null,
		},
		{
			label: "Warranty Period",
			value: equipment.warrantyPeriod,
		},
		{
			label: "Usual Location (Site)",
			isSiteLookup: true,
		},
		{
			label: "Unit of Measure",
			value: equipment.maintenanceType,
		},
		{
			label: currentReadingLabel(equipment.maintenanceType),
			isCurrentReading: true,
		},
		{
			label: "Next WOF",
			value: equipment.nextWof ? formatDate(equipment.nextWof) : null,
		},
		{
			label: "Next Reg",
			value: equipment.nextReg ? formatDate(equipment.nextReg) : null,
		},
		{
			label: "Next RUCs",
			value: equipment.nextRUCs ? formatDate(equipment.nextRUCs) : null,
		},
		{
			label: "Next Service",
			value: equipment.nextService
				? formatDate(equipment.nextService)
				: null,
		},
		{
			label: "Hourly Rate",
			value: equipment.hourlyRate
				? `$${equipment.hourlyRate.toFixed(2)}`
				: null,
		},
	];

	const renderField = (row) => (
		<div className={classes.fieldBlock}>
			<Typography className={classes.label} variant="body2">
				{row.label}
			</Typography>
			{row.isStatus ? (
				<Chip
					size="small"
					label={equipment.active ? "Active" : "Inactive"}
					className={
						equipment.active
							? classes.chipActive
							: classes.chipInactive
					}
					data-cy="equipment-info-status"
				/>
			) : row.isSiteLookup ? (
				<Typography variant="h5" className={classes.value}>
					{equipment.usualLocationSiteId ? (
						<Lookup
							id={equipment.usualLocationSiteId}
							resource="sites"
							attribute="siteName"
						/>
					) : (
						"-"
					)}
				</Typography>
			) : row.isCurrentReading ? (
				<Typography variant="h5" className={classes.value}>
					{equipment.maintenanceType === "Month"
						? equipment.currentHoursKmsDate
							? moment(equipment.currentHoursKmsDate).format("Do MMM YYYY")
							: "-"
						: equipment.currentHoursKms || "-"}
					{equipment.maintenanceType !== "Month" &&
						equipment.currentHoursKmsDate && (
						<Typography
							variant="caption"
							className={classes.caption}
						>
							Last updated:{" "}
							{moment(equipment.currentHoursKmsDate).format(
								"Do MMM YYYY"
							)}
						</Typography>
					)}
				</Typography>
			) : (
				<Typography variant="h5" className={classes.value}>
					{row.value || "-"}
				</Typography>
			)}
		</div>
	);

	return (
		<>
			{canManage && (
				<EquipmentModal
					equipment={equipment}
					isOpen={showEditModal}
					onSubmit={handleModalSave}
					onClose={() => setShowEditModal(false)}
				/>
			)}
			<ExpansionPanel
				className={classes.root}
				elevation={0}
				expanded={expanded}
				onChange={onExpandedChange}
				data-cy="equipment-info-card"
			>
				<ExpansionPanelSummary
					expandIcon={
						<ExpandMoreIcon className={classes.expandIcon} />
					}
					className={classes.summary}
					aria-controls="equipment-info-panel-content"
					id="equipment-info-panel-header"
				>
					<Typography
						component="h2"
						className={classes.summaryTitle}
					>
						Details
					</Typography>
				</ExpansionPanelSummary>
				<ExpansionPanelDetails className={classes.details}>
					<Divider />
					<Grid
						container
						spacing={2}
						className={classes.fieldsGrid}
					>
						{rows.map((row, index) => (
							<Grid
								item
								xs={12}
								md={4}
								key={`${row.label}-${index}`}
							>
								{renderField(row)}
							</Grid>
						))}
						<Grid item xs={12}>
							<div className={classes.fieldBlock}>
								<Typography
									className={classes.label}
									variant="body2"
								>
									Files
								</Typography>
								<AttachmentListing
									attachments={equipment.attachments}
								/>
							</div>
						</Grid>
					</Grid>
					{expanded && canManage && (
						<div className={classes.detailsActions}>
							<StyledIconButton
								onClick={() => setShowEditModal(true)}
							>
								<EditIcon />
							</StyledIconButton>
						</div>
					)}
				</ExpansionPanelDetails>
			</ExpansionPanel>
		</>
	);
};

EquipmentSummary.propTypes = {
	equipment: PropTypes.object,
	onSave: PropTypes.func.isRequired,
	expanded: PropTypes.bool.isRequired,
	onExpandedChange: PropTypes.func.isRequired,
};

export default EquipmentSummary;
